/// <reference types="vite/client" />
/**
 * The main file for managing environment variables.
 * */
import { z } from 'zod';

declare global {
  interface ImportMetaEnv extends Environment {}
}

const envSchema = z.object({
  VITE_STORAGE_NAME: z.string(),
  VITE_API_URL: z.string().url().default('http://localhost:5274'),
  // Local-only override for the TypeScript API (`apps/api`), which serves `chat_v5`. Deployed builds leave it unset and use `VITE_API_URL`, where the ingress routes by path
  VITE_API_V2_URL: z.string().url().optional(),
  VITE_POSTHOG_API_KEY: z.string(),
  VITE_FEATURE_FLAG_DEV_PREFIX: z.string().optional(),
  VITE_GITHUB_USERNAME: z.string().optional(),
  // Full-stack dev copies backend `FEATURE_FLAG_DEV_PREFIX` or `GITHUB_USERNAME` here; `VITE_FEATURE_FLAG_DEV_PREFIX` still wins
  VITE_BACKEND_FEATURE_FLAG_DEV_PREFIX: z.string().optional(),
  VITE_AUTH0_DOMAIN: z.string(),
  VITE_AUTH0_CLIENT_ID: z.string(),
  VITE_AUTH0_AUDIENCE: z.string().url(),
  VITE_ENV: z
    .enum(['development', 'staging', 'production', 'test'])
    .default('development'),
  IS_RUNNING_IN_LOCALHOST: z.boolean().default(false),
  BASE_URL_FOR_PUBLIC_LINKS: z.string().url().optional(),
  // The web app that public links and browser sign-in open, for a staging or production build against another backend (the preview team environment). Unset keeps the environment's own web app
  VITE_PUBLIC_WEB_URL: z.string().url().optional(),
  ENABLE_DEVTOOLS: z.boolean().optional().default(false),
  VITE_ENABLE_AGENTATION: z.coerce.boolean().default(false),
  VITE_IS_STAGING_BUILD_FOR_DESKTOP: z.coerce.boolean().default(false),
  VITE_IS_EXPERIMENTAL_BUILD: z.coerce.boolean().default(false),
  VITE_CHROME_EXTENSION_ID: z.string().optional(),

  VITE_SINGULAR_SDK_KEY: z.string().optional(),
  VITE_SINGULAR_SDK_SECRET: z.string().optional(),

  VITE_PRODUCT_FRUITS_WORKSPACE_CODE: z.string().optional(),
  VITE_LOG_TAURI_EVENTS: z.coerce.boolean().default(false).optional(),
  VITE_WS_URL: z.string().url(),
  VITE_IS_TEST_RUN: z.coerce.boolean().default(false),
  VITE_LITTLEBIRD_WORKTREE_ID: z.string().optional(),
});

type Environment = z.infer<typeof envSchema>;

function validateEnv(): Environment {
  try {
    const rawEnv = import.meta.env;

    // Parse the env first (without BASE_URL_FOR_PUBLIC_LINKS)
    const partiallyParsedEnv = envSchema.partial().parse({
      ...rawEnv,
      IS_RUNNING_IN_LOCALHOST: import.meta.env.DEV,
    });

    const viteEnv = partiallyParsedEnv.VITE_ENV || 'development';

    // In dev the port is dynamic (auto-picked when 1420 is busy), so follow
    // the actual origin. Packaged debug builds load from file:// (origin
    // "null"), which would fail the .url() check below — keep the old default
    const baseUrlForPublicLinks =
      viteEnv === 'development'
        ? window.location.origin.startsWith('http')
          ? window.location.origin
          : 'http://localhost:1420'
        : rawEnv.VITE_PUBLIC_WEB_URL ||
          (viteEnv === 'staging'
            ? 'https://stage.littlebird.ai'
            : 'https://app.littlebird.ai');

    const wsUrl =
      rawEnv.VITE_WS_URL ||
      (viteEnv === 'production'
        ? 'wss://ws.littlebird.ai'
        : viteEnv === 'test'
          ? 'wss://test.local'
          : 'ws://localhost:5274/ws/socket');

    if (!wsUrl) {
      throw new Error(
        `VITE_WS_URL is required for ${viteEnv} environment. ` +
          'Please set it in your .env.local file.'
      );
    }

    const enableDevTools =
      rawEnv.VITE_ENABLE_DEVTOOLS === 'true' ||
      (viteEnv === 'development' && rawEnv.VITE_ENABLE_DEVTOOLS !== 'false');

    const parsedEnv = envSchema.parse({
      ...rawEnv,
      IS_RUNNING_IN_LOCALHOST: import.meta.env.DEV,
      BASE_URL_FOR_PUBLIC_LINKS: baseUrlForPublicLinks,
      VITE_WS_URL: wsUrl,
      ENABLE_DEVTOOLS: enableDevTools,
    });

    return parsedEnv;
  } catch (error) {
    console.error('❌ Invalid environment configuration', error);
    throw new Error('❌ Invalid environment configuration');
  }
}

const envMap = validateEnv();

export function getEnvVar<K extends keyof Environment>(key: K): Environment[K] {
  return envMap[key];
}

export const isDev = getEnvVar('VITE_ENV') === 'development';
export const isTest = getEnvVar('VITE_ENV') === 'test';
export const isStaging = getEnvVar('VITE_ENV') === 'staging';

export const isStagingBuildWithProductionBackend = Boolean(
  getEnvVar('VITE_IS_STAGING_BUILD_FOR_DESKTOP')
);

export const isExperimental = Boolean(getEnvVar('VITE_IS_EXPERIMENTAL_BUILD'));
/**
 * Returns true if the app is running in any non-production environment
 * where debug features should be enabled (used for i.e conditional logging).
 */

export const isDebug =
  isStagingBuildWithProductionBackend || isStaging || isDev;

export const isRunningInLocalhost = getEnvVar('IS_RUNNING_IN_LOCALHOST');

export const isProd = getEnvVar('VITE_ENV') === 'production';

export const env = isStagingBuildWithProductionBackend
  ? 'staging-build'
  : getEnvVar('VITE_ENV');

export const posthogApiKey = getEnvVar('VITE_POSTHOG_API_KEY');

export const singularSdkKey = getEnvVar('VITE_SINGULAR_SDK_KEY');
export const singularSdkSecret = getEnvVar('VITE_SINGULAR_SDK_SECRET');

export const isE2ETestEnv = Boolean(getEnvVar('VITE_IS_TEST_RUN'));

export const worktreeId = import.meta.env.VITE_LITTLEBIRD_WORKTREE_ID || null;
